import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, { useEffect, useRef, useState } from "react";
import styles from "./pieChart.module.css";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { TOptionData, TQuestionData } from "../../../services/types/user.types";
import { ColorPalette } from "../../../utils/constants";
import ReactDOM from "react-dom";

interface ChartProps {
    data: TQuestionData;
    total: number;
    isPopup?: boolean;
}

const PieChartComp: React.FC<ChartProps> = ({ data, total, isPopup }) => {
    const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
    const [hoveredLegend, setHoveredLegend] = useState<number | null>(null);
    const spanRefs = useRef<(HTMLSpanElement | null)[]>([]);
    const [chartData, setChartData] = useState([{ name: "", value: -1, color: "#fff" }]);
    const [isEmpty, setIsEmpty] = useState<boolean>(false);

    const getColor = (index: number) => {
        const usedColors: string[] = chartData.map(i => i.color);
        if (index < ColorPalette.length) {
            const color = ColorPalette[index];
            usedColors.push(color);
            return color;
        }
        let color = "#" + Math.floor(Math.random() * 16777215).toString(16) + "";
        usedColors.push(color);
        return color;
    };

    const renderCustomLegend = () => {
        return chartData.map((entry, index) => (
            <div
                key={index}
                className={styles.colorItem}
                style={{ position: "relative", display: "flex", alignItems: "center" }}
                onMouseEnter={() => {
                    const el = spanRefs.current[index];
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        setTooltipPos({
                            top: rect.bottom + 5,
                            left: rect.left,
                        });
                    }
                    setHoveredLegend(index);
                }}
                onMouseLeave={() => {
                    setHoveredLegend(null);
                    setTooltipPos(null);
                }}
            >
                <div
                    className={styles.colorBox}
                    style={{ backgroundColor: entry.color }}
                />
                <span
                    ref={(el) => (spanRefs.current[index] = el)}
                    className={styles.legendText}
                    style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "180px",
                        display: "inline-block",
                    }}
                >
                    {entry.name}
                </span>
            </div>
        ));
    };

    const renderPieLabel = ({
        cx, cy, midAngle, innerRadius, outerRadius, percent, index
    }: any) => {
        if (percent === 0) return null;
        const RADIAN = Math.PI / 180;
        // Calculate label position
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill="#fff"
                textAnchor="middle"
                dominantBaseline="central"
                fontWeight="light"
                fontSize={14}
                style={{ pointerEvents: "none" }}
            >
                {`${Math.round(percent * 100)}%`}
            </text>
        );
    };

    useEffect(() => {
        setChartData(data.options.map((option: TOptionData, index) => ({
            name: option.desc,
            value: option.answerPercentage,
            color: getColor(index)
        })));

        if (!data.options.find(op => op.answersCount > 0))
            setIsEmpty(true);
        else
            setIsEmpty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <div className={styles.chartContainer}>
            <div className={styles.colorMap}>
                {renderCustomLegend()}
            </div>
            <ResponsiveContainer
                width={isPopup ? "100%" : 230}
                height={isPopup ? 420 : "100%"}
                minHeight={isPopup ? "320px" : "162px"}
                className={`${isPopup ? styles.ResponsiveContainerPopup : ""}`}
            >
                {!isEmpty ? (
                    isPopup ? (
                        <PieChart>
                            <Pie
                                data={chartData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={150}
                                label={renderPieLabel}
                                labelLine={false}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                        </PieChart>
                    ) : (
                        <PieChart width={400} height={500}>
                            <Pie
                                data={chartData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={110}
                                label={renderPieLabel}
                                labelLine={false}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                        </PieChart>
                    )
                ) : (
                    <h4 className={styles.chart_message}>{t('NO_DATA_TO_DISPLAY')}</h4>
                )}
            </ResponsiveContainer>
            {
                hoveredLegend !== null &&
                tooltipPos &&
                ReactDOM.createPortal(
                    <div
                        className={styles.legendTooltip}
                        style={{
                            position: "absolute",
                            top: tooltipPos.top,
                            left: tooltipPos.left,
                        }}
                    >
                        {chartData[hoveredLegend ?? 0]?.name ?? t('UNKNOWN')}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default PieChartComp;